import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import { getHelpItemCopy } from "./helpCatalog";
import sidebarImg from "../../img/overview/CP4.png";
import cpImg from "../../img/Criticalpath/CP1.png";

const HELP_IMAGES = {
  sidebar: sidebarImg,
  criticalPath: cpImg,
};

export default function HelpItem({
  item,
  expanded,
  onToggle,
  isHere,
  canAccess = true,
  onGoTo,
  onOpenRelated,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const copy = getHelpItemCopy(t, item.id);
  const image = item.image ? HELP_IMAGES[item.image] : null;
  const accent = theme.palette.primary.main;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={onToggle}
      sx={{
        mb: 1,
        borderRadius: "12px !important",
        border: "1px solid",
        borderColor: isHere ? accent : alpha(accent, 0.22),
        bgcolor: alpha(accent, isHere ? 0.1 : 0.04),
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: accent }} />}
        sx={{
          "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center" },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0, width: "100%", pr: 1 }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: accent, flex: 1, minWidth: 0 }}
          >
            {copy.title}
          </Typography>
          {isHere ? (
            <Chip
              size="small"
              label={t("help.youAreHere")}
              sx={{
                height: 22,
                fontWeight: 800,
                bgcolor: accent,
                color: theme.palette.primary.contrastText,
              }}
            />
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>
        <Stack spacing={1.5}>
          {item.route ? (
            <Button
              size="small"
              variant="outlined"
              color={canAccess ? "primary" : "warning"}
              endIcon={
                canAccess ? (
                  <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                ) : (
                  <LockOutlinedIcon sx={{ fontSize: 16 }} />
                )
              }
              onClick={() => onGoTo(item)}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              {canAccess ? t("help.goToModule") : t("help.noAccessButton")}
            </Button>
          ) : null}

          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 800, color: accent }}
            >
              {t("help.what")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {copy.what}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 800, color: accent }}
            >
              {t("help.how")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {copy.how}
            </Typography>
          </Box>

          {copy.steps.length > 0 ? (
            <Box>
              {copy.steps.map((step, index) => (
                <Box
                  key={`${item.id}-step-${index}`}
                  sx={{ display: "flex", alignItems: "flex-start", mb: 0.9 }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      border: "2px solid",
                      borderColor: "error.main",
                      color: "error.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: 12,
                      mr: 1.25,
                      mt: 0.1,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {step}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}

          {image ? (
            <Box
              component="img"
              src={image}
              alt={copy.title}
              sx={{
                width: "100%",
                borderRadius: 2,
                objectFit: "contain",
                border: "1px solid",
                borderColor: "divider",
              }}
            />
          ) : null}

          {copy.rules.length > 0 ? (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, color: accent }}
              >
                {t("help.rules")}
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
                {copy.rules.map((rule) => (
                  <Typography
                    key={rule}
                    component="li"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {rule}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : null}

          {item.related?.length ? (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, color: "text.secondary" }}
              >
                {t("help.related")}
              </Typography>
              <Stack
                direction="row"
                spacing={0.75}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.75 }}
              >
                {item.related.map((relatedId) => (
                  <Chip
                    key={relatedId}
                    size="small"
                    clickable
                    label={t(`help.items.${relatedId}.title`)}
                    onClick={() => onOpenRelated(relatedId)}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
